/**
 * Klien HTTP untuk API Skava.
 *
 * Sesi memakai cookie HttpOnly, sehingga tidak ada token yang perlu — atau
 * boleh — disimpan di JavaScript. Yang dikerjakan berkas ini adalah
 * menyertakan kredensial, membaca amplop galat seragam, dan menyalakan
 * kembali galat itu sebagai ApiError yang dapat dicocokkan berdasarkan kode.
 */

export interface ApiErrorDetail {
  field?: string
  message: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: ApiErrorDetail[]
  readonly correlationId?: string

  constructor(status: number, code: string, message: string, details: ApiErrorDetail[] = [], correlationId?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.correlationId = correlationId
  }

  /** isUnauthorized menandai sesi yang sudah berakhir. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** fieldErrors memetakan galat validasi ke nama kolom formulir. */
  get fieldErrors(): Record<string, string> {
    const map: Record<string, string> = {}
    for (const detail of this.details) {
      if (detail.field) map[detail.field] = detail.message
    }
    return map
  }
}

const BASE_URL = '/api/v1'

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Wajib untuk seluruh operasi yang mengubah uang (SDLC §8.6). */
  idempotencyKey?: string
  signal?: AbortSignal
  query?: Record<string, string | number | boolean | undefined | null>
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }

  const encoded = params.toString()
  return encoded ? `${url}?${encoded}` : url
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }

  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey

  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    // Kredensial disertakan agar cookie sesi ikut terkirim.
    credentials: 'include',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload: unknown = text ? JSON.parse(text) : undefined

  if (!response.ok) {
    const envelope = payload as
      | { error?: { code?: string; message?: string; details?: ApiErrorDetail[]; correlation_id?: string } }
      | undefined
    const error = envelope?.error

    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? 'Terjadi kesalahan yang tidak terduga.',
      error?.details ?? [],
      error?.correlation_id,
    )
  }

  return payload as T
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/**
 * newIdempotencyKey membuat kunci baru untuk satu maksud pengguna.
 *
 * Kunci dibuat sekali per niat — bukan per percobaan kirim — sehingga
 * percobaan ulang setelah jaringan putus memakai kunci yang sama dan tidak
 * pernah memindahkan dana dua kali.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}
